import { scaleLog } from '@visx/scale';
import { Text } from '@visx/text';
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';
import { useCallback } from 'react';

import { fixedValueGenerator, getRotationDegree } from './WordCloud.helpers';
import { CloudWord, SpiralProps, Word, WordData } from './WordCloud.types';
import { DEFAULT_HEIGHT, DEFAULT_SPIRAL, DEFAULT_WIDTH, DEFAULT_WORD_PADDING } from './WordCloudWidget.config';

export type WordCloudProps = {
  data: Word[];
  width?: number;
  height?: number;
  font?: string | ((word: Word, index: number) => string);
  fontStyle?: string | number;
  fontWeight?: string | number;
  fontSize?: number | ((word: Word, index: number) => number);
  rotate?: boolean;
  spiral?: SpiralProps;
  padding?: number | ((word: Word, index: number) => number);
  // override the the cloudwords with export data
  exportData?: CloudWord[];
  // called when the wordcloud is done rendering the words and their positions
  onComplete?: (words: CloudWord[]) => void;
  random?: () => number;
};

const WordCloud = ({
  data,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  font = 'serif',
  fontStyle = 'normal',
  fontWeight = 'normal',
  rotate = false,
  spiral = DEFAULT_SPIRAL,
  padding = DEFAULT_WORD_PADDING,
  exportData = undefined,
  onComplete,
}: WordCloudProps) => {
  // get the scale to adjust the font size based on the data
  const fontScale = scaleLog({
    domain: [Math.min(...data.map((w) => w.value)), Math.max(...data.map((w) => w.value))],
    range: [10, 50],
  });

  //  get the scale to adjust the font size based on the container size
  const getScale = useCallback((width: number, height: number) => {
    return Math.min(width / DEFAULT_WIDTH, height / DEFAULT_HEIGHT);
  }, []);

  // set the font size based on the value of the word
  const fontSizeSetter = useCallback(
    (datum: WordData) => fontScale(datum.value) * getScale(width, height),
    [fontScale, getScale, height, width],
  );

  return (
    <Wordcloud
      words={data}
      width={width}
      height={height}
      fontSize={fontSizeSetter}
      font={font}
      padding={padding}
      spiral={spiral}
      rotate={rotate ? getRotationDegree : 0}
      random={fixedValueGenerator}
    >
      {(cloudWords) => {
        let renderCloudWords: CloudWord[] = cloudWords as CloudWord[];

        // call the onComplete callback with the cloudWords
        if (onComplete && cloudWords?.length > 0) onComplete(cloudWords as CloudWord[]);

        // the export can pass the cloudWords directly with precalculated positions
        if (exportData) renderCloudWords = exportData;

        return renderCloudWords.map((w, i) => (
          <Text
            key={i}
            fill={data[i]?.color ?? 'black'}
            textAnchor={'middle'}
            transform={`translate(${w.x}, ${w.y}) rotate(${w.rotate})`}
            fontSize={w.size}
            fontFamily={w.font}
            fontWeight={fontWeight}
            fontStyle={fontStyle}
          >
            {w.text}
          </Text>
        ));
      }}
    </Wordcloud>
  );
};

export default WordCloud;
